/**
 * compareTo — a metric condition's OPTIONAL right-hand side: another metric
 * over its own date range, scaled by a percent factor. `uEPC < 80% of uEPC
 * (last-30-days)` is the canonical trend rule.
 *
 * One module owns the contract for BOTH rule systems (FO tag rules and the
 * exclude/action rule trees) because fetch-time and eval-time — on server AND
 * client — must derive the SAME cube key from the same window, or the RHS
 * reads an empty bag. Presets key by their bare id and custom windows by
 * their literal bounds, so a compareTo window that any formula group, rule
 * group or tag rule already measures costs ZERO extra tracker requests: the
 * cube builders dedupe every source through one `seen` set of these keys.
 *
 * Missing RHS data is FAIL-CLOSED: the comparison threshold becomes NaN and
 * the condition is false — for every operator, `!=` included. Comparing
 * against a bag that does not exist must never degrade into comparing
 * against zero.
 * For Media Devoted | Created By Ciro Autiero
 */

/** RHS for a FO tag-rule metric condition (tag-side window vocabulary). */
export interface TagCompareTo {
  field: string;
  /** Absent = same window as the rule itself. */
  dateRangePreset?: string;
  fromDate?: string | null;
  toDate?: string | null;
  toDateIsToday?: boolean;
  /**
   * OPTIONAL tolerance in percent — absent = compare the metrics directly
   * (×1). Any value above 0 is legal: cross-unit comparisons (EPC vs CR)
   * legitimately need very small or very large percents.
   */
  percent?: number;
  /**
   * What the RHS reads. Absent/"self" = the SAME element's numbers (the
   * original behaviour). "total" = the blended TOTAL across the whole
   * population for that window — the number the stats tables' TOTAL row
   * shows (raw metrics summed, ratio metrics re-derived from the sums).
   */
  against?: "self" | "total";
}

/** RHS for an exclude/action rule metric leaf (group-window vocabulary). */
export interface RuleCompareTo {
  metric: string;
  /** Absent = the group's window (inherit). */
  dateRange?: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  percent?: number;
  /** See {@link TagCompareTo.against}: absent/"self" = this element,
   *  "total" = the blended population TOTAL for the window. */
  against?: "self" | "total";
}

export function comparePercentFactor(percent?: number): number {
  return (Number.isFinite(percent) ? (percent as number) : 100) / 100;
}

/** Window in normal form, whatever vocabulary it arrived in. */
export interface CompareWindow {
  preset?: string;
  from?: string | null;
  to?: string | null;
  toDateIsToday?: boolean;
}

export function tagCompareWindow(ct: TagCompareTo): CompareWindow {
  return {
    preset: ct.dateRangePreset,
    from: ct.fromDate,
    to: ct.toDate,
    toDateIsToday: ct.toDateIsToday,
  };
}

export function ruleCompareWindow(ct: RuleCompareTo): CompareWindow {
  return {
    preset: ct.dateRange,
    from: ct.dateRangeCustomFrom,
    to: ct.dateRangeCustomTo,
  };
}

export type ResolvedCompareWindow =
  | { kind: "inherit" }
  | { kind: "key"; cubeKey: string }
  | { kind: "invalid" };

/**
 * No preset → inherit the LHS window (the tag rule's / the group's). A preset
 * keys by its bare id; `custom` keys by its literal bounds — `today` stands in
 * for an open end so the key is stable while the fetch spec resolves the real
 * date. Custom with incomplete bounds is INVALID (condition false, nothing
 * fetched), never a silent inherit.
 */
export function resolveCompareWindow(w: CompareWindow): ResolvedCompareWindow {
  const preset = w.preset?.trim();
  if (!preset) return { kind: "inherit" };
  if (preset !== "custom") return { kind: "key", cubeKey: preset };
  const from = w.from?.trim();
  const to = w.toDateIsToday ? "today" : w.to?.trim();
  if (!from || !to) return { kind: "invalid" };
  return { kind: "key", cubeKey: `custom:${from}:${to}` };
}

/** RHS raw value × percent factor; anything non-finite propagates as NaN. */
export function compareThreshold(rhsRaw: unknown, percent?: number): number {
  const rhs = typeof rhsRaw === "number" && Number.isFinite(rhsRaw) ? rhsRaw : Number.NaN;
  return rhs * comparePercentFactor(percent);
}

/** Accessors for the population TOTAL bags (against: "total"). */
export interface RuleCompareTotals {
  /** TOTAL over the group's OWN window (the inherit target). */
  inherit: () => Record<string, number> | undefined;
  /** TOTAL over a named window's cube key. */
  byKey: (cubeKey: string) => Record<string, number> | undefined;
}

/**
 * Exclude-side threshold: resolve the RHS bag (inherit = the group bag the
 * caller already holds), refuse an EMPTY bag — a window with no data must not
 * read as zero — then scale. NaN on any failure. `against:"total"` reads the
 * blended population TOTAL through `totals`; a caller that provides none
 * fail-closes every total-comparison rather than misreading the element.
 */
export function ruleCompareThreshold(
  ct: RuleCompareTo,
  inheritBag: Record<string, number>,
  resolve: (cubeKey: string) => Record<string, number>,
  totals?: RuleCompareTotals,
): number {
  const win = resolveCompareWindow(ruleCompareWindow(ct));
  if (win.kind === "invalid") return Number.NaN;
  const bag = compareAgainstTotal(ct)
    ? win.kind === "key"
      ? totals?.byKey(win.cubeKey)
      : totals?.inherit()
    : win.kind === "key"
      ? resolve(win.cubeKey)
      : inheritBag;
  if (!bag || Object.keys(bag).length === 0) return Number.NaN;
  return compareThreshold(bag[ct.metric], ct.percent);
}

/** Absent = direct comparison; present = any finite value above 0. */
function validPercent(p: unknown): boolean {
  if (p === undefined) return true;
  return typeof p === "number" && Number.isFinite(p) && p > 0;
}

function validAgainst(a: unknown): boolean {
  return a === undefined || a === "self" || a === "total";
}

/** True when the comparison reads the population TOTAL, not the element. */
export function compareAgainstTotal(ct: {
  against?: "self" | "total";
}): boolean {
  return ct.against === "total";
}

/** `presets` undefined = skip the membership check (caller has no catalog). */
function validWindow(win: CompareWindow, presets?: ReadonlyArray<string>): boolean {
  if (!win.preset) return true;
  if (presets && !presets.includes(win.preset)) return false;
  return resolveCompareWindow(win).kind !== "invalid";
}

/** Shared by the tag DTO, the editors and the AI-paste validation. */
export function isValidTagCompareTo(
  raw: unknown,
  fields: ReadonlyArray<string>,
  presets: ReadonlyArray<string>,
): boolean {
  if (typeof raw !== "object" || raw === null) return false;
  const ct = raw as TagCompareTo;
  if (typeof ct.field !== "string" || !fields.includes(ct.field)) return false;
  if (!validPercent(ct.percent)) return false;
  if (!validAgainst(ct.against)) return false;
  return validWindow(tagCompareWindow(ct), presets);
}

export function isValidRuleCompareTo(
  raw: unknown,
  metrics?: ReadonlyArray<string>,
  presets?: ReadonlyArray<string>,
): boolean {
  if (typeof raw !== "object" || raw === null) return false;
  const ct = raw as RuleCompareTo;
  if (typeof ct.metric !== "string" || ct.metric.length === 0) return false;
  if (metrics && metrics.length > 0 && !metrics.includes(ct.metric)) return false;
  if (!validPercent(ct.percent)) return false;
  if (!validAgainst(ct.against)) return false;
  return validWindow(ruleCompareWindow(ct), presets);
}
